import { FormControl, InputLabel, MenuItem, Select, SxProps, Theme } from "@mui/material";
import ConfigUtils from "../../../utils/Config";
import IEntidadFacturacion from "../../../types/EntidadFacturacion";

// Sentinel interno: MUI Select no admite `null` como value, así que la entidad principal
// (entidadId: null) se representa aquí y se traduce de vuelta a null en onChange.
const PRINCIPAL_SENTINEL = "__principal__";

export interface IEntidadConsulta {
  entidadId: string | null;
  nombre: string;
  activa: boolean;
  etiqueta: string; // lo que se muestra: el nombre, con "(desactivada)" y el NIF si hace falta distinguirla
}

// Entidades para CONSULTAR listados/informes: incluye también las desactivadas — su historial
// sigue siendo consultable aunque no puedan emitir facturas nuevas. Distinto del selector de
// crear (SelectorEntidadFacturacion, solo activas). La lista sale de la configuración del
// usuario, no de las facturas cargadas.
// Tras un cambio de titular, la identidad anterior queda desactivada y puede conservar el mismo nombre que la
// actual: entonces se añade el NIF para distinguirlas.
export const getEntidadesConsulta = (): IEntidadConsulta[] => {
  const principal = ConfigUtils.getDatosFacturacion();
  const entidades = [
    { entidadId: null, nombre: principal?.nombre || "Principal", nif: principal?.nif ?? "", activa: true },
    ...ConfigUtils.getDatosFacturacionAdicional().map((e: IEntidadFacturacion) => ({ entidadId: e._id, nombre: e.nombre, nif: e.nif, activa: e.active })),
  ];
  const repetido = (nombre: string) => entidades.filter(e => e.nombre.trim().toLowerCase() === nombre.trim().toLowerCase()).length > 1;
  return entidades.map(({ nif, ...e }) => ({
    ...e,
    etiqueta: `${e.nombre}${repetido(e.nombre) && nif ? ` · ${nif}` : ""}${e.activa ? "" : " (desactivada)"}`,
  }));
};

// Fragmento seguro para nombres de fichero, usado para distinguir la descarga de cada entidad.
export const slugifyEntidadNombre = (nombre: string): string =>
  nombre
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

interface ISelectorEntidadConsultaProps {
  value: string | null;
  onChange: (entidadId: string | null) => void;
  sx?: SxProps<Theme>;
}

// Selector de entidad para CONSULTAR (listados/informes): selección única, siempre con un valor
// (por defecto la principal), oculto si solo hay una entidad.
const SelectorEntidadConsulta: React.FC<ISelectorEntidadConsultaProps> = ({ value, onChange, sx }) => {
  const entidades = getEntidadesConsulta();

  if (entidades.length <= 1) return null;

  return (
    <FormControl sx={{ width: { xs: "100%", sm: 160 }, ...sx }} size="small">
      <InputLabel sx={{ fontSize: 13 }}>Entidad</InputLabel>
      <Select
        label="Entidad"
        value={value === null ? PRINCIPAL_SENTINEL : value}
        onChange={e => onChange(e.target.value === PRINCIPAL_SENTINEL ? null : e.target.value)}
        sx={{ "& .MuiSelect-select": { fontSize: 13 } }}
      >
        {entidades.map(ent => (
          <MenuItem key={ent.entidadId ?? PRINCIPAL_SENTINEL} value={ent.entidadId ?? PRINCIPAL_SENTINEL}>
            {ent.etiqueta}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectorEntidadConsulta;
